import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, Modal, Form, Select, DatePicker, Row, Col, Space } from 'antd';
import {
  PlusOutlined, EyeOutlined, ExperimentOutlined, DeleteOutlined, MoreOutlined,
  ImportOutlined, ExportOutlined, ClockCircleOutlined, SyncOutlined, CheckCircleOutlined,
  ArrowRightOutlined, TeamOutlined, FileDoneOutlined, BankOutlined,
} from '@ant-design/icons';
import { Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import bilateralesService from '../../services/bilateralesService';
import { formatDate } from '../../variables/constants';
import KpiCards from '../../components/KpiCards';
import PageHeader from '../../components/PageHeader';
import { filtreSansAccents } from '../../utils/rechercheTexte';
import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';

const fmtFcfa = (v) => v ? new Intl.NumberFormat(localeIntl()).format(v) + ' FCFA' : '-';

const STATUT = avecLibelleTraduit({
  PLANIFIEE: { color: 'blue', icon: <ClockCircleOutlined /> },
  EN_COURS: { color: 'orange', icon: <SyncOutlined /> },
  CLOTUREE: { color: 'default', icon: <CheckCircleOutlined /> },
}, 'bilaterales:statuts');

const DIRECTION = avecLibelleTraduit({
  RECEPTION: { color: 'green', icon: <ImportOutlined /> },
  DEPLACEMENT: { color: 'geekblue', icon: <ExportOutlined /> },
}, 'bilaterales:liste.directions');

export default function ListRencontres() {
  const { t } = useTranslation('bilaterales');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [bureaux, setBureaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState();
  const [paysFilter, setPaysFilter] = useState();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await bilateralesService.list({ statut: statutFilter, paysCode: paysFilter });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  const loadBureaux = async () => {
    try {
      const res = await axiosInstance.get('/v1/organismes/type/BUREAU_HOMOLOGUE');
      setBureaux(res.data?.data || []);
    } catch (_) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statutFilter, paysFilter]);
  useEffect(() => { loadBureaux(); }, []);

  const [seedLoading, setSeedLoading] = useState(false);
  const genererDemo = () => {
    Modal.confirm({
      title: t('liste.demo.titre'),
      content: (
        <div>
          <p><Trans t={t} i18nKey="liste.demo.cree" components={{ 1: <strong /> }} /></p>
          <ul style={{ paddingLeft: 18, margin: '4px 0' }}>
            <li><Trans t={t} i18nKey="liste.demo.cloturee" components={{ 1: <strong /> }} /></li>
            <li><Trans t={t} i18nKey="liste.demo.enCours" components={{ 1: <strong /> }} /></li>
            <li><Trans t={t} i18nKey="liste.demo.planifiee" components={{ 1: <strong /> }} /></li>
          </ul>
          <p style={{ color: '#6b7280', fontSize: 12 }}>
            {t('liste.demo.repartition')}
          </p>
        </div>
      ),
      okText: t('liste.demo.generer'),
      cancelText: t('actions.annuler'),
      onOk: async () => {
        setSeedLoading(true);
        try {
          const data = await bilateralesService.seedDemo();
          toast.success(
            t('liste.demo.crees', { nbRencontres: data.nbRencontres, nbSessions: data.nbSessions, nbDossiers: data.nbDossiers })
          );
          load();
        } catch (_) {
        } finally { setSeedLoading(false); }
      },
    });
  };

  const creer = async (vals) => {
    try {
      const bureau = bureaux.find(b => b.organismeTrackingId === vals.bureauHomologueTrackingId);
      const payload = {
        direction: vals.direction,
        paysHomologueCode: bureau?.codePays || vals.paysHomologueCode,
        bureauHomologueTrackingId: vals.bureauHomologueTrackingId,
        dateDebut: vals.periode[0].format('YYYY-MM-DD'),
        dateFin: vals.periode[1].format('YYYY-MM-DD'),
      };
      const created = await bilateralesService.create(payload);
      toast.success(t('liste.rencontreCreee', { reference: created.reference }));
      setOpen(false);
      form.resetFields();
      navigate(`/bilaterales/${created.rbTrackingId}`);
    } catch (e) {
      // toast géré par axios
    }
  };

  const columns = [
    {
      title: t('champs.reference'),
      dataIndex: 'reference',
      render: (v, row) => (
        <Button type="link" onClick={() => navigate(`/bilaterales/${row.rbTrackingId}`)} style={{ paddingLeft: 0 }}>
          {v}
        </Button>
      ),
    },
    {
      title: t('champs.direction'), dataIndex: 'direction',
      render: (v) => {
        const d = DIRECTION[v];
        return d ? <Tag color={d.color} icon={d.icon}>{d.label}</Tag> : <Tag>{v}</Tag>;
      },
    },
    {
      title: t('champs.bureauHomologue'), dataIndex: 'bureauHomologueRaisonSociale',
      render: (v) => v ? <span><BankOutlined style={{ color: '#9ca3af', marginRight: 6 }} />{v}</span> : '-',
    },
    { title: t('champs.pays'), dataIndex: 'paysHomologueCode', width: 80 },
    {
      title: t('champs.periode'),
      render: (_, r) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {formatDate(r.dateDebut)}
          <ArrowRightOutlined style={{ fontSize: 10, color: '#9ca3af', margin: '0 6px' }} />
          {formatDate(r.dateFin)}
        </span>
      ),
    },
    { title: t('champs.compagnies'), dataIndex: 'nbSessions', align: 'center', width: 110 },
    { title: t('champs.dossiers'), dataIndex: 'nbDossiers', align: 'center', width: 100 },
    {
      title: t('champs.bapTotal'),
      dataIndex: 'totalBapTotal',
      align: 'right',
      render: (v) => v ? <strong style={{ color: '#1a6b3c' }}>{fmtFcfa(v)}</strong> : '-',
    },
    {
      title: t('champs.statut'),
      dataIndex: 'statut',
      width: 130,
      render: (v) => {
        const s = STATUT[v];
        return s ? <Tag color={s.color} icon={s.icon}>{s.label}</Tag> : <Tag>{v}</Tag>;
      },
    },
    {
      title: '',
      width: 90,
      render: (_, r) => (
        <Space size={0}>
          <Button icon={<EyeOutlined />} size="small" type="text"
            onClick={() => navigate(`/bilaterales/${r.rbTrackingId}`)} />
          <Dropdown
            menu={{
              items: [
                {
                  key: 'delete',
                  danger: true,
                  icon: <DeleteOutlined />,
                  label: t('actions.supprimer'),
                  onClick: () => supprimerRencontre(r),
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} size="small" type="text" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const supprimerRencontre = (rb) => {
    Modal.confirm({
      title: t('suppression.titre', { reference: rb.reference }),
      content: t('suppression.contenuListe', { nbSessions: rb.nbSessions || 0, nbDossiers: rb.nbDossiers || 0 }),
      okText: t('actions.supprimer'),
      okButtonProps: { danger: true },
      cancelText: t('actions.annuler'),
      onOk: async () => {
        await bilateralesService.supprimer(rb.rbTrackingId);
        toast.success(t('suppression.succes'));
        load();
      },
    });
  };

  const nb = (statut) => items.filter(r => r.statut === statut).length;
  const bapCumule = items.reduce((s, r) => s + (Number(r.totalBapTotal) || 0), 0);

  return (
    <Layout title={t('liste.titre')}>
      {/* Pas de pagination serveur ici : `items` contient toute la liste renvoyee
          par l'API, sa longueur est donc le compte exact. */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<TeamOutlined />}
        compte={items.length}
        unite={t('liste.unite')}
      />

      <KpiCards
        items={[
          { label: t('liste.kpi.rencontres'), value: items.length, icon: <TeamOutlined />, color: '#1a6b3c', bg: '#f0fdf4', suffix: t('liste.kpi.suffixeRencontres', { count: items.length }) },
          { label: t('liste.kpi.enCours'), value: nb('EN_COURS'), icon: <SyncOutlined />, color: '#d97706', bg: '#fffbeb' },
          { label: t('liste.kpi.cloturees'), value: nb('CLOTUREE'), icon: <FileDoneOutlined />, color: '#0891b2', bg: '#ecfeff' },
          { label: t('liste.kpi.bapCumule'), value: fmtFcfa(bapCumule), icon: <BankOutlined />, color: '#059669', bg: '#ecfdf5' },
        ]}
      />

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="auto">
            <Space>
              <Select
                placeholder={t('liste.filtres.statut')}
                allowClear
                style={{ width: 160 }}
                onChange={setStatutFilter}
                options={[
                  { value: 'PLANIFIEE', label: t('statuts.PLANIFIEE') },
                  { value: 'EN_COURS', label: t('statuts.EN_COURS') },
                  { value: 'CLOTUREE', label: t('statuts.CLOTUREE') },
                ]}
              />
              <Select
                placeholder={t('liste.filtres.pays')}
                allowClear
                style={{ width: 220 }}
                onChange={setPaysFilter}
                options={bureaux.map(b => ({ value: b.codePays, label: `${b.codePays} - ${b.raisonSociale}` }))}
              />
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ExperimentOutlined />} onClick={genererDemo} loading={seedLoading}>
                {t('liste.actions.donneesDemo')}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
                {t('liste.actions.nouvelleRencontre')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          rowKey="rbTrackingId"
          loading={loading}
          dataSource={items}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 20, showTotal: (total) => t('liste.total', { count: total }) }}
        />
      </div>

      <Modal
        open={open}
        title={t('liste.modale.titre')}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        okText={t('liste.modale.creer')}
        cancelText={t('actions.annuler')}
        width={620}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={creer}>
          <Form.Item label={t('champs.direction')} name="direction" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'RECEPTION', label: t('directionsChoix.RECEPTION') },
                { value: 'DEPLACEMENT', label: t('directionsChoix.DEPLACEMENT') },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('champs.bureauHomologue')} name="bureauHomologueTrackingId" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={filtreSansAccents}
              optionFilterProp="label"
              options={bureaux.map(b => ({
                value: b.organismeTrackingId,
                label: `${b.codePays} - ${b.raisonSociale}`,
              }))}
            />
          </Form.Item>
          <Form.Item label={t('champs.periode')} name="periode" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
